import { zodResolver } from "@hookform/resolvers/zod";
import { Search } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import { useAdminOrgs, useCreateOrg } from "@/api/hooks/admin";
import { errorMessage } from "@/api/errors";
import { EmptyState } from "@/components/EmptyState";
import { ErrorNotice } from "@/components/ErrorNotice";
import { Field, FormError } from "@/components/Field";
import { PageHeader } from "@/components/PageHeader";
import { PageLoading } from "@/components/Spinner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { formatDate } from "@/lib/dates";
import { usePageCrumbs } from "@/app/layouts/shellContext";

const schema = z.object({ name: z.string().trim().min(1, "admin.orgs.nameRequired"), slug: z.string().trim() });
type Values = z.infer<typeof schema>;

function CreateOrgDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateOrg();
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { name: "", slug: "" } });
  const { errors } = form.formState;
  const onSubmit = form.handleSubmit((v) =>
    create.mutate(
      { name: v.name, ...(v.slug ? { slug: v.slug } : {}) },
      {
        onSuccess: (org) => {
          toast.success(t("admin.orgs.created"));
          onOpenChange(false);
          navigate(`/admin/orgs/${org.id}`);
        },
      },
    ),
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("admin.orgs.create")}</DialogTitle>
          <DialogDescription>{t("admin.orgs.createIntro")}</DialogDescription>
        </DialogHeader>
        <form noValidate onSubmit={onSubmit} className="flex flex-col gap-4">
          <FormError message={create.isError ? errorMessage(create.error, t("errors.generic")) : null} />
          <Field label={t("admin.orgs.name")} error={errors.name?.message ? t(errors.name.message) : null}>
            {(p) => <Input {...p} {...form.register("name")} />}
          </Field>
          <Field label={t("admin.orgs.slug")} hint={t("admin.orgs.slugHint")}>
            {(p) => <Input {...p} {...form.register("slug")} />}
          </Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {t("admin.orgs.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function OrgsPage() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const orgs = useAdminOrgs(q.trim());
  usePageCrumbs([{ label: t("common.nav.admin") }, { label: t("admin.nav.orgs") }]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("admin.orgs.title")}
        description={t("admin.orgs.intro")}
        actions={<Button onClick={() => setOpen(true)}>{t("admin.orgs.create")}</Button>}
      />
      <div className="relative max-w-md">
        <Search aria-hidden="true" className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-fg-subtle" />
        <Input
          type="search"
          aria-label={t("admin.orgs.search")}
          placeholder={t("admin.orgs.search")}
          className="pl-12"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      {orgs.isPending ? <PageLoading /> : null}
      {orgs.isError ? <ErrorNotice error={orgs.error} /> : null}
      {orgs.data && orgs.data.length === 0 ? <EmptyState title={t("admin.orgs.empty")} /> : null}
      {orgs.data && orgs.data.length > 0 ? (
        <Table>
          <THead>
            <TR>
              <TH>{t("admin.orgs.colName")}</TH>
              <TH>{t("admin.orgs.colSlug")}</TH>
              <TH>{t("admin.orgs.colCountry")}</TH>
              <TH>{t("admin.orgs.colStatus")}</TH>
              <TH>{t("admin.orgs.colCreated")}</TH>
            </TR>
          </THead>
          <TBody>
            {orgs.data.map((o) => (
              <TR key={o.id}>
                <TD className="font-medium">
                  <Link to={`/admin/orgs/${o.id}`}>{o.name}</Link>
                </TD>
                <TD className="font-mono text-sm text-fg-muted">{o.slug}</TD>
                <TD>{o.country}</TD>
                <TD>
                  {o.deleted_at ? (
                    <Badge variant="outline">{t("admin.orgs.deleted")}</Badge>
                  ) : o.is_active ? (
                    <Badge>{t("admin.orgs.active")}</Badge>
                  ) : (
                    <Badge variant="grey">{t("admin.orgs.inactive")}</Badge>
                  )}
                </TD>
                <TD className="whitespace-nowrap text-fg-muted">{formatDate(o.created_at)}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      ) : null}
      <CreateOrgDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
